'use client';

import { Box, Container } from '@mui/material';
import type { SchoolYearKey } from '@ocode/domain';
import { useDtbSelectableSubjectByYear } from '@repo-ui/hooks/useDtbSelectableSubjectByYear';
import { useSysKeyNames } from '@repo-ui/hooks/useSysKeyNames';
import { routerPushI18n } from '@repo-ui/router';
import { useEffect, useMemo } from 'react';
import DtbPageBanner from './components/DtbPageBanner';
import { DtbTocContainer } from './components/DtbTocContainer';
import { SearchBox } from './components/SearchBox';

interface Props {
  yearKey: SchoolYearKey;
  subjectKey?: string;
}

export function DtbTocFeature(props: Props) {
  const { yearKey, subjectKey } = props;
  const selectableSubjectByYear = useDtbSelectableSubjectByYear();

  // 전달된 yearKey에서 선택가능한 교과서 KEY들
  const selectableSubjectKeys = useMemo<string[] | undefined>(() => {
    if (selectableSubjectByYear && yearKey) {
      return selectableSubjectByYear[yearKey];
    }
    return undefined;
  }, [selectableSubjectByYear, yearKey]);

  // 선택 가능한 학년 KEY들
  const selectableYearKeys = useMemo<SchoolYearKey[] | undefined>(() => {
    if (selectableSubjectByYear) {
      return Object.keys(selectableSubjectByYear) as SchoolYearKey[];
    }
    return undefined;
  }, [selectableSubjectByYear]);

  // 교과서 자동 선택
  useEffect(() => {
    if (!selectableSubjectKeys || selectableSubjectKeys.length === 0) {
      return;
    }

    // 교과서 파라미터가 전달되지 않으면, 선택 가능한 교과서들중 하나를 선택
    // 또는 선택한 교과서가 selectable 하지 않다면, 선택 가능한 교과서들중 하나를 선택
    if (!subjectKey || !selectableSubjectKeys.includes(subjectKey)) {
      routerPushI18n('/dtb/list', yearKey, selectableSubjectKeys[0]);
    }
  }, [selectableSubjectKeys, yearKey, subjectKey]);

  // 모든 교과서의 이름들
  const { data: allSubjectNames } = useSysKeyNames('SCHL_SBJCT');

  return (
    <Box
      className="DtbTocFeature-root"
      sx={{
        bgcolor: '#fff',
        pb: 8,
      }}
    >
      <DtbPageBanner />

      <Container sx={{ mt: 6 }}>
        {selectableSubjectKeys && allSubjectNames && selectableYearKeys && (
          <SearchBox
            selectedYearKey={yearKey}
            selectedSubjectKey={subjectKey}
            selectableSubjectKeys={selectableSubjectKeys}
            selectableYearKeys={selectableYearKeys}
            allSubjectNames={allSubjectNames}
          />
        )}

        {yearKey && subjectKey && (
          <DtbTocContainer yearKey={yearKey} subjectKey={subjectKey} sx={{ mt: 8 }} />
        )}
      </Container>
    </Box>
  );
}
